import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { isEqual } from "lodash";
import {
  Map,
  LayersControl,
  TileLayer,
  Polygon,
  Polyline,
  CircleMarker,
  Rectangle,
  ZoomControl,
  Tooltip,
} from "react-leaflet";

import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Select, { SelectOption } from "@amzn/meridian/select";
import Modal from "@amzn/meridian/modal";
import Loader from "@amzn/meridian/loader";
import {
  colorBlue400,
  colorPurple200,
  colorOrange400,
  colorGreen400,
  colorRed400,
} from "@amzn/meridian-tokens/base/color";

import { keys } from "i18n";
import { LOCATION_TYPE, LOCATION_TYPES, CONVEX_LOCATION_TYPES } from "app-constants";
import { createYardMap } from "store/yardMap/yardMapActions";
import { validatePolygonForLocationType } from "utils/geoJsonUtils";
import {
  areCollinear,
  computeParallelogramCorner,
  subdivideParallelogram,
  latLngToMeters,
  polygonsOverlap,
  isConvexPolygon,
} from "utils/drawingUtils";

import FindYardForm from "./FindYardForm";
import PolygonEditorPanel from "./PolygonEditorPanel";

const MODES = { IDLE: "idle", DRAWING: "drawing", BATCH: "batch", SELECT: "select" };
const CLOSE_THRESHOLD_PX = 10;

// Generate a unique location code that doesn't collide with existing ones
const generateUniqueCode = (existingPolygons) => {
  const existingCodes = new Set(existingPolygons.map((p) => p.locationCode));
  let i = existingPolygons.length + 1;
  while (existingCodes.has(`PS${i}`)) {
    i++;
  }
  return `PS${i}`;
};

export default function ManualMapCreator({
  yardId,
  onCancel,
  initialPolygons,
  initialCenter,
  onSave,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { creatingYardMap, errorCreatingYardMap } = useSelector((state) => state.yardMapData);
  const secrets = useSelector((state) => state.secrets);

  const isEditMode = !!(initialPolygons && initialCenter);
  const [center, setCenter] = useState(isEditMode ? initialCenter : [39.8283, -98.5795]);
  const [zoom, setZoom] = useState(isEditMode ? 18 : 4);
  const [polygons, setPolygons] = useState(isEditMode ? initialPolygons : []);
  const [currentVertices, setCurrentVertices] = useState([]);
  const [cursorPos, setCursorPos] = useState(null);
  const [mode, setMode] = useState(MODES.IDLE);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
  const [hasSearched, setHasSearched] = useState(isEditMode);
  const [batchPoints, setBatchPoints] = useState([]);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [draggingVertex, setDraggingVertex] = useState(null);
  const [selectStart, setSelectStart] = useState(null);
  const [selectEnd, setSelectEnd] = useState(null);
  // Pending new polygon(s) awaiting user confirmation via dialog
  const [pendingNew, setPendingNew] = useState(null);
  const [pendingCode, setPendingCode] = useState("");
  const [pendingType, setPendingType] = useState(LOCATION_TYPE.PARKING_SLIP);
  const [pendingError, setPendingError] = useState(null);
  const [batchCount, setBatchCount] = useState("2");

  const polygonClickedRef = useRef(false);
  const selectStartRef = useRef(null);
  const justExitedSelectRef = useRef(false);
  const polygonsRef = useRef(polygons);
  polygonsRef.current = polygons;
  const mapRef = useRef(null);

  const handleMouseMove = useCallback(
    (e) => {
      if (
        (mode === MODES.DRAWING && currentVertices.length > 0) ||
        (mode === MODES.BATCH && batchPoints.length > 0 && batchPoints.length < 3)
      ) {
        setCursorPos([e.latlng.lat, e.latlng.lng]);
      }
    },
    [mode, currentVertices.length, batchPoints.length]
  );

  const isNearFirstVertex = useCallback(
    (latlng) => {
      if (currentVertices.length < 3 || !mapRef.current) return false;
      const map = mapRef.current.leafletElement;
      const firstPx = map.latLngToContainerPoint(currentVertices[0]);
      const clickPx = map.latLngToContainerPoint([latlng.lat, latlng.lng]);
      const dx = firstPx.x - clickPx.x;
      const dy = firstPx.y - clickPx.y;
      return Math.sqrt(dx * dx + dy * dy) < CLOSE_THRESHOLD_PX;
    },
    [currentVertices]
  );

  // Selection uses document-level listeners + overlay div.
  // The overlay captures mousedown. Document-level mousemove/mouseup
  // ensure we always get the events even if the mouse leaves the overlay.
  const selectedIdsRef = useRef(new Set());

  useEffect(() => {
    if (mode !== MODES.SELECT) return;

    const onDocMouseMove = (e) => {
      if (!selectStartRef.current) return;
      const map = mapRef.current?.leafletElement;
      if (!map) return;
      const container = map.getContainer();
      const rect = container.getBoundingClientRect();
      const latlng = map.containerPointToLatLng([e.clientX - rect.left, e.clientY - rect.top]);
      setSelectEnd([latlng.lat, latlng.lng]);
    };

    const onDocMouseUp = (e) => {
      const start = selectStartRef.current;
      selectStartRef.current = null;
      setSelectStart(null);
      setSelectEnd(null);

      const map = mapRef.current?.leafletElement;
      if (!map || !start) {
        justExitedSelectRef.current = true;
        setTimeout(() => {
          justExitedSelectRef.current = false;
        }, 100);
        setMode(MODES.IDLE);
        return;
      }

      const container = map.getContainer();
      const rect = container.getBoundingClientRect();
      const endX = e.clientX - rect.left;
      const endY = e.clientY - rect.top;
      const startPx = map.latLngToContainerPoint(start);
      const dist = Math.sqrt(Math.pow(startPx.x - endX, 2) + Math.pow(startPx.y - endY, 2));

      if (dist > 10) {
        const latlng = map.containerPointToLatLng([endX, endY]);
        const end = [latlng.lat, latlng.lng];
        const minLat = Math.min(start[0], end[0]);
        const maxLat = Math.max(start[0], end[0]);
        const minLng = Math.min(start[1], end[1]);
        const maxLng = Math.max(start[1], end[1]);

        const ids = new Set();
        polygonsRef.current.forEach((p) => {
          if (
            p.vertices.some(
              ([lat, lng]) => lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng
            )
          ) {
            ids.add(p.id);
          }
        });

        if (ids.size > 0) {
          selectedIdsRef.current = ids;
          setSelectedIds(ids);
          setSelectedId(null);
        }
      }

      justExitedSelectRef.current = true;
      setTimeout(() => {
        justExitedSelectRef.current = false;
      }, 100);
      setMode(MODES.IDLE);
    };

    document.addEventListener("mousemove", onDocMouseMove);
    document.addEventListener("mouseup", onDocMouseUp);

    return () => {
      document.removeEventListener("mousemove", onDocMouseMove);
      document.removeEventListener("mouseup", onDocMouseUp);
    };
  }, [mode]);

  const handleOverlayMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const map = mapRef.current?.leafletElement;
    if (!map) return;
    const container = map.getContainer();
    const rect = container.getBoundingClientRect();
    const latlng = map.containerPointToLatLng([e.clientX - rect.left, e.clientY - rect.top]);
    selectStartRef.current = [latlng.lat, latlng.lng];
    setSelectStart([latlng.lat, latlng.lng]);
    setSelectEnd(null);
  };

  const handleMapClick = useCallback(
    (e) => {
      setError(null);

      // Skip if we just exited SELECT mode — selection was already handled
      if (justExitedSelectRef.current) {
        justExitedSelectRef.current = false;
        return;
      }

      if (mode === MODES.IDLE) {
        if (polygonClickedRef.current) {
          polygonClickedRef.current = false;
          return;
        }
        setSelectedId(null);
        setSelectedIds(new Set());
        return;
      }

      if (mode === MODES.DRAWING) {
        const point = [e.latlng.lat, e.latlng.lng];

        if (isNearFirstVertex(e.latlng)) {
          const vertices = [...currentVertices];
          setCurrentVertices([]);
          setCursorPos(null);
          setMode(MODES.IDLE);
          // Show dialog for new polygon details
          setPendingNew({ type: "single", vertices });
          setPendingCode(generateUniqueCode(polygons));
          setPendingType(LOCATION_TYPE.PARKING_SLIP);
          setPendingError(null);
          return;
        }

        setCurrentVertices((prev) => [...prev, point]);
        return;
      }

      if (mode === MODES.BATCH) {
        const point = [e.latlng.lat, e.latlng.lng];
        if (batchPoints.length < 3) {
          const newPoints = [...batchPoints, point];
          if (newPoints.length === 3 && areCollinear(newPoints[0], newPoints[1], newPoints[2])) {
            setError(t(keys.COLLINEAR_POINTS_ERROR));
            return;
          }
          setBatchPoints(newPoints);
          setCursorPos(null);
          if (newPoints.length === 3) {
            // Auto-open dialog when 3 points placed
            setPendingNew({ type: "batch", points: newPoints });
            setPendingCode("PS");
            setPendingType(LOCATION_TYPE.PARKING_SLIP);
            setBatchCount("2");
            setPendingError(null);
          }
        }
      }
    },
    [mode, currentVertices, batchPoints, isNearFirstVertex, t]
  );

  const handleLocate = (coords) => {
    setCenter(coords);
    setZoom(18);
    setHasSearched(true);
    const map = mapRef.current?.leafletElement;
    if (map) map.setView(coords, 18);
  };

  const startDrawing = () => {
    setMode(MODES.DRAWING);
    setCurrentVertices([]);
    setCursorPos(null);
    setSelectedId(null);
  };

  const startBatch = () => {
    setMode(MODES.BATCH);
    setBatchPoints([]);
    setSelectedId(null);
    setSelectedIds(new Set());
  };

  const cancelCurrentAction = () => {
    setMode(MODES.IDLE);
    setCurrentVertices([]);
    setCursorPos(null);
    setBatchPoints([]);
    setSelectStart(null);
    setSelectEnd(null);
    const map = mapRef.current?.leafletElement;
    if (map) map.dragging.enable();
  };

  const handleUpdatePolygon = (updated) => {
    setPolygons((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  const handleConfirmPending = () => {
    if (!pendingNew) return;
    const code = pendingCode.trim();
    if (!code) {
      setPendingError(t(keys.REQUIRED_ERROR_MESSAGE));
      return;
    }

    if (pendingNew.type === "single") {
      try {
        validatePolygonForLocationType(pendingNew.vertices, pendingType);
      } catch (e) {
        setPendingError(e.message);
        return;
      }
      if (CONVEX_LOCATION_TYPES.has(pendingType) && !isConvexPolygon(pendingNew.vertices)) {
        setPendingError(t(keys.NON_CONVEX_POLYGON));
        return;
      }
      if (polygons.some((p) => p.locationCode === code)) {
        setPendingError(`${t(keys.GEOJSON_ERROR_DUPLICATE_LOCATION_CODES)}: ${code}`);
        return;
      }
      setPolygons((prev) => [
        ...prev,
        {
          id: uuidv4(),
          locationCode: code,
          locationType: pendingType,
          vertices: pendingNew.vertices,
        },
      ]);
    } else {
      // batch — compute strips from points + count
      const n = parseInt(batchCount, 10);
      if (isNaN(n) || n < 1) {
        setPendingError(t(keys.REQUIRED_ERROR_MESSAGE));
        return;
      }
      const [p1, p2, p3] = pendingNew.points;
      const p4 = computeParallelogramCorner(p1, p2, p3);
      const strips = subdivideParallelogram([p1, p2, p3, p4], n);
      // Find highest existing number with this prefix to avoid conflicts
      let startNum = 0;
      polygons.forEach((p) => {
        if (p.locationCode.startsWith(code)) {
          const num = parseInt(p.locationCode.slice(code.length), 10);
          if (!isNaN(num) && num > startNum) startNum = num;
        }
      });
      const newPolygons = strips.map((verts, i) => ({
        id: uuidv4(),
        locationCode: `${code}${startNum + i + 1}`,
        locationType: pendingType,
        vertices: verts,
      }));
      setPolygons((prev) => [...prev, ...newPolygons]);
      setBatchPoints([]);
      setMode(MODES.IDLE);
    }
    setPendingNew(null);
  };

  const handleCancelPending = () => {
    if (pendingNew?.type === "batch") {
      setBatchPoints([]);
      setMode(MODES.IDLE);
    }
    setPendingNew(null);
    setPendingError(null);
  };

  const handleDeletePolygon = (id) => {
    setPolygons((prev) => prev.filter((p) => p.id !== id));
    setSelectedId(null);
  };

  const handleVertexMouseDown = (polyId, vertexIdx) => {
    setDraggingVertex({ polyId, vertexIdx });
  };

  useEffect(() => {
    if (!draggingVertex) return;

    const map = mapRef.current?.leafletElement;
    if (!map) return;

    map.dragging.disable();

    const onMove = (e) => {
      const { polyId, vertexIdx } = draggingVertex;
      setPolygons((prev) =>
        prev.map((p) => {
          if (p.id !== polyId) return p;
          const newVerts = [...p.vertices];
          newVerts[vertexIdx] = [e.latlng.lat, e.latlng.lng];
          return { ...p, vertices: newVerts };
        })
      );
    };

    const onUp = () => {
      map.dragging.enable();
      // Keep polygon selected after drag — set flag so map click doesn't deselect
      polygonClickedRef.current = true;
      setDraggingVertex(null);
    };

    map.on("mousemove", onMove);
    map.on("mouseup", onUp);
    return () => {
      map.off("mousemove", onMove);
      map.off("mouseup", onUp);
    };
  }, [draggingVertex]);

  const handleSave = () => {
    if (polygons.length === 0) {
      setError(t(keys.NO_POLYGONS_DRAWN));
      return;
    }

    // Check for non-convex parking slips / dock doors
    const nonConvex = polygons.filter(
      (p) => CONVEX_LOCATION_TYPES.has(p.locationType) && !isConvexPolygon(p.vertices)
    );
    if (nonConvex.length > 0) {
      setError(`${t(keys.NON_CONVEX_POLYGON)}: ${nonConvex.map((p) => p.locationCode).join(", ")}`);
      return;
    }

    // Check for overlapping polygons
    const overlaps = [];
    for (let i = 0; i < polygons.length; i++) {
      for (let j = i + 1; j < polygons.length; j++) {
        if (polygonsOverlap(polygons[i].vertices, polygons[j].vertices)) {
          overlaps.push(`${polygons[i].locationCode} & ${polygons[j].locationCode}`);
        }
      }
    }
    if (overlaps.length > 0) {
      setError(`${t(keys.OVERLAPPING_POLYGONS)}: ${overlaps.join(", ")}`);
      return;
    }

    setShowSaveConfirm(true);
  };

  const confirmSave = () => {
    setShowSaveConfirm(false);
    const anchor = isEditMode ? initialCenter : polygons[0].vertices[0];
    const locations = polygons.map((p) => {
      const polygon = latLngToMeters(p.vertices, anchor);
      const loc = {
        locationCode: p.locationCode,
        locationType: p.locationType,
        polygon,
      };
      if (
        p.locationType === LOCATION_TYPE.PARKING_SLIP ||
        p.locationType === LOCATION_TYPE.DOCK_DOOR
      ) {
        loc.parkingDirection = 0;
      }
      return loc;
    });

    if (onSave) {
      onSave({ gpsPosition: { latitude: anchor[0], longitude: anchor[1] }, locations });
    } else {
      dispatch(
        createYardMap({
          yardId,
          gpsPosition: { latitude: anchor[0], longitude: anchor[1] },
          locations,
        })
      );
    }
  };

  const handleCancel = () => {
    const hasChanges = isEditMode
      ? currentVertices.length > 0 || batchPoints.length > 0 || !isEqual(polygons, initialPolygons)
      : polygons.length > 0 || currentVertices.length > 0 || batchPoints.length > 0;

    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      onCancel();
    }
  };

  const selectedPolygon = polygons.find((p) => p.id === selectedId);

  const previewLine =
    cursorPos &&
    ((mode === MODES.DRAWING &&
      currentVertices.length > 0 && [currentVertices[currentVertices.length - 1], cursorPos]) ||
      (mode === MODES.BATCH &&
        batchPoints.length > 0 &&
        batchPoints.length < 3 && [batchPoints[batchPoints.length - 1], cursorPos]) ||
      null);

  const batchPreview =
    mode === MODES.BATCH && batchPoints.length === 3
      ? [...batchPoints, computeParallelogramCorner(batchPoints[0], batchPoints[1], batchPoints[2])]
      : null;

  const dialogHeight = window.innerHeight - 270;

  return (
    <Column spacing="medium">
      <FindYardForm onLocate={handleLocate} />
      <Row spacing="small" alignmentHorizontal="left">
        <Button
          type="primary"
          onClick={startDrawing}
          disabled={!hasSearched || mode !== MODES.IDLE}
        >
          {t(keys.DRAW_POLYGON)}
        </Button>
        <Button
          type="secondary"
          onClick={startBatch}
          disabled={!hasSearched || mode !== MODES.IDLE}
        >
          {t(keys.BATCH_DRAW)}
        </Button>
        <Button
          type="secondary"
          onClick={() => {
            setMode(MODES.SELECT);
            setSelectedId(null);
            setSelectedIds(new Set());
          }}
          disabled={!hasSearched || mode !== MODES.IDLE || polygons.length === 0}
        >
          {t(keys.SELECT_AREA)}
        </Button>
        {mode === MODES.DRAWING || mode === MODES.BATCH ? (
          <Button type="secondary" onClick={cancelCurrentAction}>
            {t(keys.CANCEL_DRAWING)}
          </Button>
        ) : null}
        {selectedIds.size > 0 && mode === MODES.IDLE && (
          <Button type="secondary" onClick={() => setShowDeleteSelectedConfirm(true)}>
            {t(keys.DELETE_SELECTED)}
          </Button>
        )}
      </Row>
      {error && (
        <Alert type="error" size="small">
          {error}
        </Alert>
      )}
      {errorCreatingYardMap && <Alert type="error">{errorCreatingYardMap}</Alert>}
      <Row
        widths={["fill", selectedPolygon ? "300px" : "0px"]}
        alignmentVertical="top"
        spacing="none"
      >
        <div style={{ position: "relative" }}>
          <Column height={dialogHeight}>
            <Map
              ref={mapRef}
              center={center}
              zoom={zoom}
              zoomControl={false}
              onClick={handleMapClick}
              onMousemove={handleMouseMove}
              style={{
                cursor: mode === MODES.DRAWING || mode === MODES.BATCH ? "crosshair" : undefined,
              }}
            >
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="mapbox (satellite view)">
                  <TileLayer
                    url={`https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=${secrets.mapboxApiKey}`}
                    attribution='© <a href="https://www.mapbox.com/feedback/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    maxZoom={22}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="mapbox (street view)">
                  <TileLayer
                    url={`https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${secrets.mapboxApiKey}`}
                    attribution='© <a href="https://www.mapbox.com/feedback/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    maxZoom={22}
                  />
                </LayersControl.BaseLayer>

                <LayersControl.BaseLayer name="HERE (satellite view)">
                  <TileLayer
                    url={`https://maps.hereapi.com/v3/background/mc/{z}/{x}/{y}/png?style=satellite.day&apiKey=${secrets.hereApiKey}`}
                    maxZoom={20}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="HERE (street view)">
                  <TileLayer
                    url={`https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png?apiKey=${secrets.hereApiKey}`}
                    maxZoom={20}
                  />
                </LayersControl.BaseLayer>

                <LayersControl.BaseLayer name="OSM (street view)">
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    maxZoom={19}
                  />
                </LayersControl.BaseLayer>
              </LayersControl>
              <ZoomControl position="bottomright" />

              {/* Completed polygons — render non-selected first, selected last (on top) */}
              {polygons
                .filter((p) => p.id !== selectedId && !selectedIds.has(p.id))
                .map((p) => {
                  const isInvalid =
                    CONVEX_LOCATION_TYPES.has(p.locationType) && !isConvexPolygon(p.vertices);
                  return (
                    <Polygon
                      key={p.id}
                      positions={p.vertices}
                      color={isInvalid ? colorRed400 : colorPurple200}
                      fillOpacity={0.4}
                      onClick={() => {
                        if (mode === MODES.IDLE) {
                          polygonClickedRef.current = true;
                          setSelectedIds(new Set());
                          setSelectedId(p.id);
                        }
                      }}
                    >
                      <Tooltip sticky>{p.locationCode}</Tooltip>
                    </Polygon>
                  );
                })}
              {/* Multi-selected polygons */}
              {polygons
                .filter((p) => selectedIds.has(p.id) && p.id !== selectedId)
                .map((p) => (
                  <Polygon
                    key={p.id}
                    positions={p.vertices}
                    color={colorOrange400}
                    fillOpacity={0.3}
                    onClick={() => {
                      if (mode === MODES.IDLE) {
                        polygonClickedRef.current = true;
                        setSelectedIds(new Set());
                        setSelectedId(p.id);
                      }
                    }}
                  >
                    <Tooltip sticky>{p.locationCode}</Tooltip>
                  </Polygon>
                ))}
              {/* Primary selected polygon (on top) */}
              {selectedPolygon && (
                <Polygon
                  key={selectedPolygon.id}
                  positions={selectedPolygon.vertices}
                  color={
                    CONVEX_LOCATION_TYPES.has(selectedPolygon.locationType) &&
                    !isConvexPolygon(selectedPolygon.vertices)
                      ? colorRed400
                      : colorOrange400
                  }
                  fillOpacity={0.4}
                  onClick={(e) => {
                    if (mode === MODES.IDLE) {
                      polygonClickedRef.current = true;
                    }
                  }}
                >
                  <Tooltip sticky>{selectedPolygon.locationCode}</Tooltip>
                </Polygon>
              )}

              {/* Draggable vertices for selected polygon */}
              {selectedPolygon &&
                mode === MODES.IDLE &&
                selectedPolygon.vertices.map((v, i) => (
                  <CircleMarker
                    key={`v-${selectedPolygon.id}-${i}`}
                    center={v}
                    radius={4}
                    color={colorOrange400}
                    fillColor="white"
                    fillOpacity={1}
                    onMousedown={() => handleVertexMouseDown(selectedPolygon.id, i)}
                  />
                ))}

              {/* In-progress polygon outline */}
              {currentVertices.length > 1 && (
                <Polyline positions={currentVertices} color={colorGreen400} dashArray="6" />
              )}

              {/* Vertex markers for in-progress polygon */}
              {currentVertices.map((v, i) => (
                <CircleMarker
                  key={`cv-${i}`}
                  center={v}
                  radius={i === 0 && currentVertices.length >= 3 ? 6 : 5}
                  color={i === 0 && currentVertices.length >= 3 ? colorRed400 : colorGreen400}
                  fillColor="white"
                  fillOpacity={1}
                />
              ))}

              {/* Preview line to cursor */}
              {previewLine && (
                <Polyline positions={previewLine} color={colorGreen400} dashArray="4" weight={1} />
              )}

              {/* Batch points and connecting lines */}
              {batchPoints.length > 1 && (
                <Polyline positions={batchPoints} color={colorBlue400} dashArray="6" />
              )}
              {batchPoints.map((p, i) => (
                <CircleMarker
                  key={`bp-${i}`}
                  center={p}
                  radius={6}
                  color={colorBlue400}
                  fillColor="white"
                  fillOpacity={1}
                />
              ))}

              {/* Batch preview parallelogram */}
              {batchPreview && (
                <Polygon
                  positions={batchPreview}
                  color={colorBlue400}
                  fillOpacity={0.2}
                  dashArray="6"
                />
              )}

              {/* Selection rectangle */}
              {selectStart && selectEnd && (
                <Rectangle
                  bounds={[selectStart, selectEnd]}
                  color={colorBlue400}
                  fillOpacity={0.1}
                  dashArray="4"
                  weight={1}
                />
              )}
            </Map>
          </Column>
          {/* Transparent overlay for selection rectangle — sits on top of map, bypasses Leaflet entirely */}
          {mode === MODES.SELECT && (
            <div
              onMouseDown={handleOverlayMouseDown}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                cursor: "crosshair",
                zIndex: 1000,
                userSelect: "none",
              }}
            />
          )}
        </div>

        {/* Side panel */}
        <div>
          {selectedPolygon && (
            <PolygonEditorPanel
              polygon={selectedPolygon}
              allPolygons={polygons}
              onUpdate={handleUpdatePolygon}
              onDelete={handleDeletePolygon}
              onDeselect={() => setSelectedId(null)}
            />
          )}
        </div>
      </Row>
      {/* Bottom action bar */}
      <Row spacing="small" alignmentHorizontal="right">
        <Button onClick={handleCancel} type="secondary" minWidth="80px">
          {t(keys.CANCEL_AND_DISCARD)}
        </Button>
        <Button onClick={handleSave} type="primary" minWidth="90px" disabled={creatingYardMap}>
          {creatingYardMap ? <Loader size="small" /> : t(keys.SAVE_YARD_MAP)}
        </Button>
      </Row>
      {/* New polygon dialog */}
      <Modal
        width="400px"
        bodySpacingInset="medium"
        title={pendingNew?.type === "batch" ? t(keys.BATCH_DRAW) : t(keys.CONFIRM_POLYGON)}
        open={!!pendingNew}
        onClose={handleCancelPending}
      >
        <Column spacing="medium">
          {pendingError && (
            <Alert type="error" size="small">
              {pendingError}
            </Alert>
          )}

          {pendingNew?.type === "batch" && (
            <Input
              label={t(keys.NUM_SUBDIVISIONS)}
              value={batchCount}
              onChange={(v) => {
                setBatchCount(v);
                setPendingError(null);
              }}
              type="number"
            />
          )}

          {pendingNew?.type !== "batch" && (
            <Input
              label={t(keys.ENTER_LOCATION_CODE)}
              value={pendingCode}
              onChange={(v) => {
                setPendingCode(v);
                setPendingError(null);
              }}
            />
          )}

          <Select
            label={t(keys.SELECT_LOCATION_TYPE)}
            value={pendingType}
            onChange={(v) => {
              setPendingType(v);
              setPendingError(null);
            }}
          >
            {LOCATION_TYPES.map((lt) => (
              <SelectOption key={lt} value={lt} label={t(lt)} />
            ))}
          </Select>

          <Row alignmentHorizontal="right">
            <Button type="secondary" minWidth="80px" onClick={handleCancelPending}>
              {t(keys.CANCEL)}
            </Button>
            <Button type="primary" minWidth="90px" onClick={handleConfirmPending}>
              {pendingNew?.type === "batch" ? t(keys.GENERATE_SPOTS) : t(keys.CONFIRM_POLYGON)}
            </Button>
          </Row>
        </Column>
      </Modal>
      {/* Save confirmation modal */}
      <Modal
        width="400px"
        bodySpacingInset="medium"
        title={t(keys.SAVE_MAP)}
        open={showSaveConfirm}
        onClose={() => setShowSaveConfirm(false)}
      >
        <Text>{t(keys.CONFIRM_SAVE_MAP)}</Text>
        <Row alignmentHorizontal="right" spacingInset="large none none none">
          <Button type="secondary" minWidth="80px" onClick={() => setShowSaveConfirm(false)}>
            {t(keys.CANCEL)}
          </Button>
          <Button type="primary" minWidth="90px" onClick={confirmSave}>
            {t(keys.SAVE)}
          </Button>
        </Row>
      </Modal>
      {/* Delete selected confirmation modal */}
      <Modal
        width="400px"
        bodySpacingInset="medium"
        title={t(keys.DELETE_SELECTED)}
        open={showDeleteSelectedConfirm}
        onClose={() => setShowDeleteSelectedConfirm(false)}
      >
        <Text>{t(keys.CONFIRM_DELETE_SELECTED)}</Text>
        <Row alignmentHorizontal="right" spacingInset="large none none none">
          <Button
            type="secondary"
            minWidth="80px"
            onClick={() => setShowDeleteSelectedConfirm(false)}
          >
            {t(keys.CANCEL)}
          </Button>
          <Button
            type="primary"
            minWidth="90px"
            onClick={() => {
              const toDelete = new Set(selectedIds);
              if (selectedId) toDelete.add(selectedId);
              setPolygons((prev) => prev.filter((p) => !toDelete.has(p.id)));
              setSelectedId(null);
              setSelectedIds(new Set());
              setShowDeleteSelectedConfirm(false);
            }}
          >
            {t(keys.DELETE)}
          </Button>
        </Row>
      </Modal>
      {/* Cancel confirmation modal */}
      <Modal
        width="400px"
        bodySpacingInset="medium"
        title={t(keys.WARNING)}
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
      >
        <Text>{t(keys.UNSAVED_POLYGONS_WARNING)}</Text>
        <Row alignmentHorizontal="right" spacingInset="large none none none">
          <Button type="secondary" minWidth="80px" onClick={() => setShowCancelConfirm(false)}>
            {t(keys.NO)}
          </Button>
          <Button type="primary" minWidth="90px" onClick={onCancel}>
            {t(keys.YES)}
          </Button>
        </Row>
      </Modal>
    </Column>
  );
}
